const DEFAULT_TIMEOUT = 10000

/**
 * Таймауты подобраны по характеру запроса.
 * SUBMIT — для изменяющих состояние POST (заявка, оплата, SMS): запас больше, чем
 * у остальных, т.к. оборванный запрос мог дойти до сервера и означать дубль заявки.
 */
export const Timeout = {
  GEO: 3000,
  SUGGEST: 5000,
  REFERENCE: 8000,
  ACTION: 10000,
  SUBMIT: 25000
}

/**
 * Ошибка таймаута запроса.
 * Отдельный тип нужен, чтобы отличать «бэкенд не ответил» от «запрос отменён»
 */
export class RequestTimeoutError extends Error {
  constructor(url, timeout) {
    super(`Request timeout ${timeout}ms: ${url}`)

    this.name = 'RequestTimeoutError'
    this.url = url
    this.timeout = timeout
  }
}

/**
 * fetch с таймаутом
 * @param {String}      url
 * @param {Object}      [options]
 * @param {Number}      [options.timeout]   таймаут, мс: по истечении запрос прерывается
 * @param {AbortSignal} [options.signal]  внешний сигнал отмены
 * @returns {Promise<Response>}
 */
export const request = (url, {
  timeout = DEFAULT_TIMEOUT,
  signal = null,
  ...init
} = {}) => {
  const controller = new AbortController()
  let isTimedOut = false

  const abortByExternalSignal = () => controller.abort()

  const timer = setTimeout(() => {
    isTimedOut = true
    controller.abort()
    cleanup()
  }, timeout)

  function cleanup() {
    clearTimeout(timer)
    signal?.removeEventListener('abort', abortByExternalSignal)
  }

  if (signal) {
    signal.aborted
      ? controller.abort()
      : signal.addEventListener('abort', abortByExternalSignal)
  }

  return fetch(url, { ...init, signal: controller.signal })
    .then(async (response) => {
      // Тело дочитываем здесь, под тем же таймаутом: fetch резолвится на заголовках,
      // и обрыв на response.json() у вызывающего ушёл бы наружу AbortError — форма
      // не распознала бы таймаут, а повтор с новым request_id создал бы дубль заявки.
      // arrayBuffer, а не text(): декодирование в UTF-8 испортило бы бинарное тело
      const body = await response.arrayBuffer()

      cleanup()

      // url в конструктор Response не передать, а он нужен для диагностики ошибок
      return Object.defineProperty(
        new Response(body.byteLength ? body : null, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        }),
        'url',
        { value: response.url }
      )
    })
    .catch((error) => {
      cleanup()
      throw isTimedOut ? new RequestTimeoutError(url, timeout) : error
    })
}
